import {
  appendObstacle,
  appendSegment,
  appendTree,
  removeGeometry
} from '../geometry/geometry-transitions';
import { appendDesignerAnnotation } from '../canvas/canvas-transitions';

export const DesignerCommandTypes = {
  SET_ANNOTATIONS: 'SET_ANNOTATIONS',
  ADD_ANNOTATION: 'ADD_ANNOTATION',
  APPEND_ANNOTATION: 'APPEND_ANNOTATION',
  REMOVE_ANNOTATION: 'REMOVE_ANNOTATION',
  NEW_SHAPE: 'NEW_SHAPE',
  SAVE_SHAPE_EDITS: 'SAVE_SHAPE_EDITS',
  ADD_POLYGON_POINT: 'ADD_POLYGON_POINT',
  REMOVE_POLYGON_POINT: 'REMOVE_POLYGON_POINT',
  DELETE_SHAPE: 'DELETE_SHAPE',
  REPLACE_SHAPES: 'REPLACE_SHAPES',
  LABEL_LINE: 'LABEL_LINE',
  SET_SEGMENTS: 'SET_SEGMENTS',
  PATCH_SEGMENT: 'PATCH_SEGMENT',
  PATCH_OBSTACLE: 'PATCH_OBSTACLE',
  PATCH_TREE: 'PATCH_TREE',
  DELETE_ALL_TREES: 'DELETE_ALL_TREES',
  IMPORT_CANVAS_DATA: 'IMPORT_CANVAS_DATA',
  PATCH_STATE: 'PATCH_STATE'
};

export const DesignerEffectTypes = {
  PERSIST: 'PERSIST',
  CHECK_SHADING: 'CHECK_SHADING',
  UPDATE_AZIMUTH: 'UPDATE_AZIMUTH',
  UPDATE_SETBACKS: 'UPDATE_SETBACKS'
};

function effect(type, payload = {}) {
  return { type, ...payload };
}

function result(state, effects = [], meta = {}) {
  return { state, effects, ...meta };
}

export function designerDistance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

export function designerPointsEqual(a, b) {
  return a?.[0] === b?.[0] && a?.[1] === b?.[1];
}

export function designerPointNearLine(point, a, b, threshold) {
  return (
    Math.abs(
      designerDistance(a, point) +
        designerDistance(b, point) -
        designerDistance(a, b)
    ) <= threshold
  );
}

export function scaleDesignerGeometry(geometry, factor) {
  return (geometry || []).map((point) => [
    Math.round(point[0] * factor),
    Math.round(point[1] * factor)
  ]);
}

export function filterDesignerDuplicatePoints(geometry = []) {
  if (geometry.length === 1) return [geometry, []];
  const removed = [];
  const filtered = geometry.filter((point, index) => {
    const next = (index + 1) % geometry.length;
    const remove = designerPointsEqual(point, geometry[next]);
    if (remove) removed.push(index);
    return !remove;
  });
  return [filtered, removed];
}

export function findDesignerCommonLine(points1 = [], points2 = []) {
  const intersection = points1.filter((point1) =>
    points2.some((point2) => designerPointsEqual(point1, point2))
  );
  if (!intersection.length) return -1;
  const indexes = [];
  points2.forEach((point, index) => {
    intersection.forEach((item) => {
      if (designerPointsEqual(point, item)) indexes.push(index);
    });
  });
  return indexes;
}

export function getDesignerEdgeOrientation(geometry, pointIndex) {
  const point1 = geometry[pointIndex];
  const point2 = geometry[(pointIndex + 1) % geometry.length];
  const center = geometry
    .reduce(
      ([centerX, centerY], [pointX, pointY]) => [
        centerX + pointX,
        centerY + pointY
      ],
      [0, 0]
    )
    .map((value) => value / geometry.length);
  const vector1X = -(point1[0] - center[0]);
  const vector1Y = -(point1[1] - center[1]);
  const vector2X = -(point2[0] - center[0]);
  const vector2Y = -(point2[1] - center[1]);
  return vector1X * vector2Y - vector1Y * vector2X >= 0 ? 'cw' : 'ccw';
}

export function getDesignerDefaultTrunkHeight(height) {
  return Number.isFinite(height) && height < 30 ? height / 4 : 10;
}

export function setDesignerAnnotations(state, annotations) {
  return {
    ...state,
    annotations: Array.isArray(annotations) ? annotations : []
  };
}

export function addDesignerAnnotation(state, position, type, params = {}) {
  return setDesignerAnnotations(
    state,
    appendDesignerAnnotation(state.annotations, position, type, params)
  );
}

export function removeDesignerAnnotation(state, key) {
  const annotations = state.annotations || [];
  const byId =
    key !== undefined &&
    key !== null &&
    annotations.some((annotation) => annotation?.id === key);
  return {
    ...state,
    annotations: byId
      ? annotations.filter((annotation) => annotation?.id !== key)
      : Number.isInteger(key)
        ? annotations.filter((_, index) => index !== key)
        : annotations
  };
}

export function addDesignerShape(state, shape, options = {}) {
  const accordion = options.accordion || state.currentAccordion;
  const scale = Number(options.scale ?? state.scale ?? 1) || 1;
  const nextShape = options.scaled
    ? {
        ...shape,
        geometry: scaleDesignerGeometry(shape.geometry, 1 / scale)
      }
    : {
        ...shape,
        geometry: (shape.geometry || []).map((point) =>
          Array.isArray(point) ? [...point] : { ...point }
        )
      };
  const effects = [
    effect(DesignerEffectTypes.CHECK_SHADING),
    effect(DesignerEffectTypes.PERSIST, { keys: [accordion] })
  ];

  if (accordion === 'segments') {
    const [geometry] = filterDesignerDuplicatePoints(nextShape.geometry);
    const segment = appendSegment([], {
      ...nextShape,
      id: options.id ?? nextShape.id,
      geometry
    })[0];
    return result(
      { ...state, segments: [...(state.segments || []), segment] },
      effects,
      { item: segment }
    );
  }
  if (accordion === 'obstacles') {
    return result(
      {
        ...state,
        obstacles: appendObstacle(state.obstacles || [], nextShape)
      },
      effects
    );
  }
  if (accordion === 'trees') {
    const resolution = Number(options.resolution ?? state.resolution ?? 1);
    const height =
      Number(
        (
          (designerDistance(nextShape.geometry[0], nextShape.geometry[1]) *
            resolution) /
          30.48
        ).toFixed(2)
      ) * 2;
    const tree = {
      ...nextShape,
      model: nextShape.model || options.treeModel || 'Sphere',
      dsm: nextShape.dsm || 'Keep',
      height: nextShape.height ?? height,
      trunkHeight:
        nextShape.trunkHeight ?? getDesignerDefaultTrunkHeight(height)
    };
    return result(
      { ...state, trees: appendTree(state.trees || [], tree) },
      effects,
      { item: tree }
    );
  }
  if (accordion === 'annotations' && nextShape.objectType) {
    const next = addDesignerAnnotation(
      state,
      {},
      nextShape.objectType,
      nextShape
    );
    return result(next, [
      effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })
    ]);
  }
  return result(state);
}

function alignValues(values, removed, length, fallback) {
  const filtered = Array.isArray(values)
    ? values.filter((_, index) => !removed.includes(index))
    : [];
  return filtered.length === length ? filtered : Array(length).fill(fallback);
}

export function saveDesignerShapeEdits(state, updated, options = {}) {
  const accordion = options.accordion || state.currentAccordion;
  const scale = Number(options.scale ?? state.scale ?? 1) || 1;
  const rawGeometry = options.scaled
    ? scaleDesignerGeometry(updated.geometry, 1 / scale)
    : updated.geometry || [];
  const [geometry, removed] = filterDesignerDuplicatePoints(rawGeometry);
  const collection = state[accordion];
  if (!Array.isArray(collection)) return result(state);
  const index = collection.findIndex((item) => item?.id === updated.id);
  if (index < 0) return result(state);
  const current = collection[index];
  const effects = [
    effect(DesignerEffectTypes.PERSIST, { keys: [accordion] }),
    effect(DesignerEffectTypes.CHECK_SHADING)
  ];

  if (accordion === 'segments') {
    if (geometry.length < 3) {
      return deleteDesignerShape(state, updated.id, { accordion });
    }
    const segments = [...collection];
    const planePatch = {};
    ['pitch', 'azimuth', 'height', 'eaveHeight'].forEach((key) => {
      if (updated[key] !== undefined) planePatch[key] = updated[key];
    });
    segments[index] = {
      ...current,
      ...planePatch,
      geometry,
      panelAxis: null,
      lines: alignValues(current.lines, removed, geometry.length, null),
      ...(Array.isArray(current.setbacks)
        ? {
            setbacks: alignValues(current.setbacks, removed, geometry.length, 0)
          }
        : {})
    };
    return result(
      { ...state, segments },
      updated.azimuth === undefined
        ? [...effects, effect(DesignerEffectTypes.UPDATE_AZIMUTH, { indexes: [index] })]
        : effects
    );
  }

  if (accordion === 'obstacles') {
    if (
      updated.shape === 'Line' &&
      (geometry.length < 2 ||
        designerDistance(geometry[0], geometry[1]) <
          (options.minimumLineLength ?? 2))
    ) {
      return result(state);
    }
    if (updated.shape === 'Polygon' && geometry.length < 3) {
      return deleteDesignerShape(state, updated.id, { accordion });
    }
    if (updated.shape === 'Point' && geometry.length !== 1) {
      return deleteDesignerShape(state, updated.id, { accordion });
    }
    const obstacles = [...collection];
    const planePatch = {};
    ['pitch', 'azimuth', 'height', 'eaveHeight'].forEach((key) => {
      if (updated[key] !== undefined) planePatch[key] = updated[key];
    });
    obstacles[index] = {
      ...current,
      ...planePatch,
      geometry: updated.shape === 'Line' ? geometry.slice(0, 2) : geometry,
      ...(updated.shape === 'Point' ? { radius: updated.radius } : {})
    };
    return result({ ...state, obstacles }, effects);
  }

  if (accordion === 'trees') {
    if (geometry.length !== 2) {
      return deleteDesignerShape(state, updated.id, { accordion });
    }
    const trees = [...collection];
    trees[index] = { ...current, geometry };
    return result({ ...state, trees }, effects);
  }

  if (accordion === 'annotations') {
    if (geometry.length !== 2) {
      return result(
        {
          ...state,
          annotations: collection.filter(
            (_, annotationIndex) => annotationIndex !== index
          )
        },
        [effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })]
      );
    }
    const annotations = [...collection];
    annotations[index] = { ...current, geometry };
    return result({ ...state, annotations }, [
      effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })
    ]);
  }

  return result(state);
}

export function addDesignerPolygonPoint(
  state,
  index,
  lineIndex,
  point,
  options = {}
) {
  const accordion = options.accordion || state.currentAccordion;
  if (!['segments', 'obstacles'].includes(accordion)) return result(state);
  const collection = state[accordion] || [];
  if (!collection[index]) return result(state);
  const scale = Number(options.scale ?? state.scale ?? 1) || 1;
  const scaledPoint = options.scaled
    ? [Math.round(point[0]), Math.round(point[1])]
    : [Math.round(point[0] / scale), Math.round(point[1] / scale)];
  const next = collection.map((item, itemIndex) => {
    if (itemIndex !== index) return item;
    const geometry = [...(item.geometry || [])];
    geometry.splice(lineIndex + 1, 0, scaledPoint);
    const lines = Array.isArray(item.lines) ? [...item.lines] : null;
    if (lines) lines.splice(lineIndex + 1, 0, null);
    const setbacks = Array.isArray(item.setbacks) ? [...item.setbacks] : null;
    if (setbacks) setbacks.splice(lineIndex + 1, 0, 0);
    return {
      ...item,
      geometry,
      ...(lines ? { lines } : {}),
      ...(setbacks ? { setbacks } : {}),
      ...(accordion === 'segments' ? { panelAxis: null } : {})
    };
  });
  return result({ ...state, [accordion]: next, polyEdit: -1 }, [
    effect(DesignerEffectTypes.PERSIST, { keys: [accordion] }),
    effect(DesignerEffectTypes.CHECK_SHADING)
  ]);
}

export function removeDesignerPolygonPoint(
  state,
  index,
  pointIndex,
  options = {}
) {
  const accordion = options.accordion || state.currentAccordion;
  if (!['segments', 'obstacles'].includes(accordion)) return result(state);
  const collection = state[accordion] || [];
  if (!collection[index]) return result(state);
  const next = collection
    .map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const geometry = (item.geometry || []).filter(
        (_, geometryIndex) => geometryIndex !== pointIndex
      );
      const lines = Array.isArray(item.lines)
        ? item.lines.filter((_, lineIndex) => lineIndex !== pointIndex)
        : null;
      const setbacks = Array.isArray(item.setbacks)
        ? item.setbacks.filter((_, setbackIndex) => setbackIndex !== pointIndex)
        : null;
      return {
        ...item,
        geometry,
        ...(lines ? { lines } : {}),
        ...(setbacks ? { setbacks } : {}),
        ...(accordion === 'segments' ? { panelAxis: null } : {})
      };
    })
    .filter(
      (item, itemIndex) => itemIndex !== index || item.geometry.length >= 3
    );
  return result({ ...state, [accordion]: next, polyEdit: -1 }, [
    effect(DesignerEffectTypes.PERSIST, { keys: [accordion] }),
    effect(DesignerEffectTypes.CHECK_SHADING)
  ]);
}

function kindForAccordion(accordion) {
  return {
    segments: 'segment',
    obstacles: 'obstacle',
    trees: 'tree',
    annotations: 'annotation'
  }[accordion];
}

export function replaceDesignerShapes(state, add, remove, options = {}) {
  const shapes = (Array.isArray(add) ? add : [add]).filter(Boolean);
  const keys = (Array.isArray(remove) ? remove : [remove]).filter(
    (key) => key != null && key !== ''
  );
  let next = state;
  keys.forEach((key) => {
    next = deleteDesignerShape(next, key, options).state;
  });
  const items = [];
  shapes.forEach((shape) => {
    const created = addDesignerShape(next, shape, {
      ...options,
      id: shape.id
    });
    next = created.state;
    if (created.item) items.push(created.item);
  });
  const accordion = options.accordion || state.currentAccordion;
  return result(
    next,
    [
      effect(DesignerEffectTypes.PERSIST, {
        keys: accordion === 'segments' ? ['segments', 'panels'] : [accordion]
      }),
      ...(accordion === 'annotations'
        ? []
        : [effect(DesignerEffectTypes.CHECK_SHADING)])
    ],
    { added: items, removedKeys: keys }
  );
}

export function deleteDesignerShape(state, key, options = {}) {
  const accordion = options.accordion || state.currentAccordion;
  const kind = kindForAccordion(accordion);
  if (!kind) return result(state);
  const next = removeGeometry(state, kind, key);
  return result(
    next,
    [
      effect(DesignerEffectTypes.PERSIST, { keys: [accordion] }),
      ...(accordion === 'annotations'
        ? []
        : [effect(DesignerEffectTypes.CHECK_SHADING)])
    ],
    { deleted: true, deletedKind: kind, deletedKey: key }
  );
}

export function labelDesignerLine(state, index, lineIndex, lines) {
  if (!state.segments?.[index]) return result(state);
  const segments = state.segments.map((segment) => ({
    ...segment,
    geometry: (segment.geometry || []).map((point) => [...point]),
    lines: Array.isArray(segment.lines)
      ? [...segment.lines]
      : Array(segment.geometry?.length || 0).fill('')
  }));
  const changedIndexes = new Set([index]);
  const segment = segments[index];
  segment.lines = [...lines];
  const point1 = segment.geometry[lineIndex];
  const point2 = segment.geometry[(lineIndex + 1) % segment.geometry.length];
  const overlapOrientation = getDesignerEdgeOrientation(
    segment.geometry,
    lineIndex
  );

  if (lines[lineIndex] !== 'Eave') {
    segments.forEach((candidate, candidateIndex) => {
      if (candidateIndex === index) return;
      const common = findDesignerCommonLine(
        segment.geometry,
        candidate.geometry
      );
      if (common === -1) return;
      if (common.length === 2) {
        const line = Math.min(...common);
        const wrapped =
          line === 0 && Math.max(...common) === candidate.geometry.length - 1;
        const targetLine = wrapped ? Math.max(...common) : line;
        const candidatePoint1 = candidate.geometry[targetLine];
        const candidatePoint2 =
          candidate.geometry[(targetLine + 1) % candidate.geometry.length];
        const orientation = getDesignerEdgeOrientation(
          candidate.geometry,
          targetLine
        );
        const matches =
          overlapOrientation === orientation
            ? designerPointsEqual(point1, candidatePoint2) &&
              designerPointsEqual(point2, candidatePoint1)
            : designerPointsEqual(point1, candidatePoint1) &&
              designerPointsEqual(point2, candidatePoint2);
        if (matches) {
          candidate.lines[targetLine] = lines[lineIndex];
          changedIndexes.add(candidateIndex);
        }
        return;
      }
      if (common.length === 1) {
        const line = common[0];
        const commonPoint = candidate.geometry[line];
        const secondPointIndex = candidate.geometry.findIndex(
          (point) =>
            designerPointNearLine(point, point1, point2, 0.5) &&
            !designerPointsEqual(point, commonPoint)
        );
        if (secondPointIndex === -1) return;
        const targetLine =
          line === 0 && secondPointIndex === candidate.geometry.length - 1
            ? secondPointIndex
            : secondPointIndex === 0 && line === candidate.geometry.length - 1
              ? line
              : Math.min(line, secondPointIndex);
        candidate.lines[targetLine] = lines[lineIndex];
        changedIndexes.add(candidateIndex);
      }
    });
  }

  return result(
    { ...state, segments },
    [
      effect(DesignerEffectTypes.PERSIST, { keys: ['segments'] }),
      effect(DesignerEffectTypes.UPDATE_AZIMUTH, {
        indexes: [...changedIndexes]
      }),
      effect(DesignerEffectTypes.UPDATE_SETBACKS),
      effect(DesignerEffectTypes.CHECK_SHADING)
    ],
    { changedIndexes: [...changedIndexes] }
  );
}

export function normalizeDesignerCanvasData(state, data = {}) {
  const segments = (data.segments || state.segments || []).map((segment) => {
    const acMonthly = Array.isArray(segment.acMonthly)
      ? [...segment.acMonthly]
      : Array(12).fill(0);
    return {
      ...segment,
      azimuth: Number(segment.azimuth),
      pitch: Number(segment.pitch),
      height: Number(segment.height),
      lines: Array(segment.geometry?.length || 0).fill(''),
      solarAccess: segment.solar_access,
      acMonthly,
      acAnnual: acMonthly.reduce((sum, value) => sum + value, 0),
      area: 0
    };
  });
  const obstacles = (data.obstacles || state.obstacles || []).map(
    (obstacle) => ({
      ...obstacle,
      radius: Number(obstacle.radius),
      height: Number(obstacle.height),
      setback: Number(obstacle.setback)
    })
  );
  const trees = (data.trees || state.trees || []).map((tree) => {
    const height = Number(tree.height);
    const rawTrunkHeight = tree.trunkHeight ?? tree.trunk_height;
    const parsedTrunkHeight =
      rawTrunkHeight === undefined || rawTrunkHeight === null
        ? undefined
        : Number(rawTrunkHeight);
    const trunkHeight = Number.isFinite(parsedTrunkHeight)
      ? Math.min(parsedTrunkHeight, height)
      : undefined;
    const rest = { ...tree };
    delete rest.trunk_height;
    return {
      ...rest,
      radius: Number(tree.radius),
      height,
      ...(trunkHeight === undefined ? {} : { trunkHeight })
    };
  });
  return {
    ...state,
    segments,
    obstacles,
    trees,
    panels: [],
    annotations: []
  };
}

export function runDesignerCommand(state, command = {}) {
  switch (command.type) {
    case DesignerCommandTypes.SET_ANNOTATIONS:
      return result(setDesignerAnnotations(state, command.annotations), [
        effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })
      ]);
    case DesignerCommandTypes.ADD_ANNOTATION:
      return result(
        addDesignerAnnotation(
          state,
          command.position,
          command.annotationType,
          command.params
        ),
        [effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })]
      );
    case DesignerCommandTypes.APPEND_ANNOTATION:
      return result(
        {
          ...state,
          annotations: [...(state.annotations || []), command.annotation]
        },
        [effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })]
      );
    case DesignerCommandTypes.REMOVE_ANNOTATION:
      return result(removeDesignerAnnotation(state, command.key), [
        effect(DesignerEffectTypes.PERSIST, { keys: ['annotations'] })
      ]);
    case DesignerCommandTypes.NEW_SHAPE:
      return addDesignerShape(state, command.shape, command.options);
    case DesignerCommandTypes.SAVE_SHAPE_EDITS:
      return saveDesignerShapeEdits(state, command.updated, command.options);
    case DesignerCommandTypes.ADD_POLYGON_POINT:
      return addDesignerPolygonPoint(
        state,
        command.index,
        command.lineIndex,
        command.point,
        command.options
      );
    case DesignerCommandTypes.REMOVE_POLYGON_POINT:
      return removeDesignerPolygonPoint(
        state,
        command.index,
        command.pointIndex,
        command.options
      );
    case DesignerCommandTypes.DELETE_SHAPE:
      return deleteDesignerShape(state, command.key, command.options);
    case DesignerCommandTypes.REPLACE_SHAPES:
      return replaceDesignerShapes(
        state,
        command.add,
        command.remove,
        command.options
      );
    case DesignerCommandTypes.LABEL_LINE:
      return labelDesignerLine(
        state,
        command.index,
        command.lineIndex,
        command.lines
      );
    case DesignerCommandTypes.SET_SEGMENTS:
      return result(
        {
          ...state,
          segments: Array.isArray(command.segments)
            ? command.segments
            : state.segments
        },
        [
          effect(DesignerEffectTypes.PERSIST, { keys: ['segments'] }),
          effect(DesignerEffectTypes.CHECK_SHADING)
        ]
      );
    case DesignerCommandTypes.PATCH_SEGMENT:
      return result(
        {
          ...state,
          segments: (state.segments || []).map((segment, index) =>
            command.index === -1 || command.index === index
              ? { ...segment, ...(command.patch || {}) }
              : segment
          )
        },
        [
          effect(DesignerEffectTypes.PERSIST, { keys: ['segments'] }),
          effect(DesignerEffectTypes.CHECK_SHADING)
        ]
      );
    case DesignerCommandTypes.PATCH_OBSTACLE:
      return result(
        {
          ...state,
          obstacles: (state.obstacles || []).map((obstacle, index) =>
            index === command.index
              ? { ...obstacle, ...(command.patch || {}) }
              : obstacle
          )
        },
        [
          effect(DesignerEffectTypes.PERSIST, { keys: ['obstacles'] }),
          effect(DesignerEffectTypes.CHECK_SHADING)
        ]
      );
    case DesignerCommandTypes.PATCH_TREE:
      return result(
        {
          ...state,
          trees: (state.trees || []).map((tree, index) =>
            index === command.index ? command.tree : tree
          )
        },
        [
          effect(DesignerEffectTypes.PERSIST, { keys: ['trees'] }),
          effect(DesignerEffectTypes.CHECK_SHADING)
        ]
      );
    case DesignerCommandTypes.DELETE_ALL_TREES:
      return result({ ...state, trees: [] }, [
        effect(DesignerEffectTypes.PERSIST, { keys: ['trees'] }),
        effect(DesignerEffectTypes.CHECK_SHADING)
      ]);
    case DesignerCommandTypes.IMPORT_CANVAS_DATA:
      return result(normalizeDesignerCanvasData(state, command.data), [
        effect(DesignerEffectTypes.PERSIST, {
          keys: ['segments', 'obstacles', 'trees', 'panels', 'annotations']
        })
      ]);
    case DesignerCommandTypes.PATCH_STATE:
      return result({ ...state, ...(command.patch || {}) });
    default:
      return result(state);
  }
}
